"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Gift,
  Users,
  Trophy,
  Share2,
  Copy,
  CheckCircle,
  Star,
  TrendingUp,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import { NavbarDemo } from "./NavBar";
import { cn } from "../lib/utils";

const POINTS_FOR_FREE_MASSAGE = 600;

export default function ReferralPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [referralSummary, setReferralSummary] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rewardsHistory, setRewardsHistory] = useState([]);
  const [recentRedemptions, setRecentRedemptions] = useState([]);
  const [isRedeeming, setIsRedeeming] = useState(false);

  const fetchRewardsData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch rewards, referral code, and history in parallel
      const [rewardsData, referralData, historyData, referralHistoryData] =
        await Promise.all([
          api.rewards
            .getRewards()
            .catch(() => ({ success: false, rewards: null })),
          api.referral
            .getOrCreateReferralCode()
            .catch(() => ({ success: false, referral: null })),
          api.rewards
            .getRewardsHistory(1, 10)
            .catch(() => ({ success: false, history: [] })),
          api.referral
            .getReferralHistory(1, 10)
            .catch(() => ({ success: false, referrals: [] })),
        ]);

      if (rewardsData.success && rewardsData.rewards) {
        setRewards(rewardsData.rewards);
        // Set recent points from rewards.recentPoints
        if (rewardsData.rewards.recentPoints) {
          setRewardsHistory(rewardsData.rewards.recentPoints);
        }
        // Set recent redemptions
        if (rewardsData.rewards.recentRedemptions) {
          setRecentRedemptions(rewardsData.rewards.recentRedemptions);
        }
      }

      if (referralData.success && referralData.referral) {
        setReferralCode(referralData.referral);
      }

      // Use history from separate API call if not already set
      if (
        historyData.success &&
        historyData.history &&
        historyData.history.length > 0
      ) {
        setRewardsHistory(historyData.history);
      }

      if (referralHistoryData.success) {
        if (referralHistoryData.referrals) {
          setReferralHistory(referralHistoryData.referrals);
        }
        if (referralHistoryData.summary) {
          setReferralSummary(referralHistoryData.summary);
        }
      }
    } catch (error) {
      console.error("Error fetching rewards data:", error);
      toast({
        title: "Error",
        description: "Failed to load rewards data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch data on mount and when user changes - similar to ClientDashboard
  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (isLoading) {
      return;
    }

    // Check localStorage directly as fallback in case AuthContext hasn't updated yet
    const hasToken = localStorage.getItem("bookatherapy_token");
    const hasUser = localStorage.getItem("bookatherapy_user");

    if (!user && !hasToken && !hasUser) {
      localStorage.setItem("redirectAfterLogin", "/referrals");
      navigate("/auth");
      return;
    }

    // If we have token/user but AuthContext hasn't updated, wait a bit
    if (!user && (hasToken || hasUser)) {
      return;
    }

    fetchRewardsData();
  }, [user, isLoading, navigate, fetchRewardsData]);

  const copyReferralLink = () => {
    if (referralCode?.referralLink) {
      navigator.clipboard.writeText(referralCode.referralLink);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferralLink = async () => {
    if (navigator.share && referralCode?.referralLink) {
      try {
        await navigator.share({
          title: "Get a Free Massage with BookaTherapy!",
          text: `Join me on BookaTherapy and we both earn points towards free massages! Use my referral link:`,
          url: referralCode.referralLink,
        });
      } catch (error) {
        console.log("Share cancelled or failed");
      }
    } else {
      copyReferralLink();
    }
  };

  const handleRedeemFreeMassage = async () => {
    // Allow redemption if user has 600+ points or has free massage credits available
    // The redemption process will convert points into a free massage credit
    const userCurrentPoints = rewards?.currentPoints || 0;
    const availableCredits = rewards?.freeMassagesAvailable || 0;

    // If user already has credits available, they can use them
    if (availableCredits > 0) {
      // Navigate to booking page where they can use the credit
      navigate("/book?useRewards=true");
      return;
    }

    // Check if user has enough points to redeem
    if (userCurrentPoints < POINTS_FOR_FREE_MASSAGE) {
      toast({
        title: "Not Enough Points",
        description: `You need ${POINTS_FOR_FREE_MASSAGE} points to redeem a free massage. You currently have ${userCurrentPoints} points. Points are earned when your bookings are confirmed by the clinic.`,
        variant: "destructive",
      });
      return;
    }

    setIsRedeeming(true);
    try {
      const response = await api.rewards.redeemFreeMassageReward();

      console.log("Redemption response:", response);

      if (response.success) {
        toast({
          title: "Free Massage Credit Redeemed!",
          description:
            response.message ||
            "Your points have been converted to a free massage credit. You can now use it when booking your next appointment.",
        });

        // Refresh rewards data
        await fetchRewardsData();

        // Navigate to booking with rewards flag
        navigate("/book?useRewards=true");
      } else {
        throw new Error(response.message || "Failed to redeem free massage");
      }
    } catch (error) {
      console.error("Error redeeming free massage:", error);

      // Extract error message from response if available
      let errorMessage = "Failed to redeem free massage. Please try again.";
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast({
        title: "Redemption Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  // Show loading while auth is loading or fetching data
  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-palette-cloud to-white flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto mb-4 animate-spin rounded-full border-4 border-palette-lime/20 border-t-palette-lime"></div>
          <p className="text-palette-space">
            {isLoading
              ? "Checking authentication..."
              : "Loading your rewards..."}
          </p>
        </div>
      </div>
    );
  }

  const currentPoints = rewards?.currentPoints || 0;
  const freeMassagesAvailable = rewards?.freeMassagesAvailable || 0;
  const pointsToNext =
    rewards?.pointsToNextFreeMassage ??
    Math.max(0, POINTS_FOR_FREE_MASSAGE - currentPoints);
  const progressPercentage = Math.min(
    (currentPoints / POINTS_FOR_FREE_MASSAGE) * 100,
    100,
  );
  // Can redeem ONLY if they have available free massages OR have reached/exceeded 600 points
  const canRedeem =
    freeMassagesAvailable > 0 || currentPoints >= POINTS_FOR_FREE_MASSAGE;

  return (
    <div className="min-h-screen bg-gradient-to-b from-palette-cloud to-white">
      <NavbarDemo />

      {/* Header */}
      <div className="bg-white border-b border-palette-smoke/50 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-palette-lime to-palette-navy flex items-center justify-center shadow-lg">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-palette-phantom">
                Rewards & Referrals
              </h1>
              <p className="text-palette-space mt-1">
                Earn points from your bookings and referrals. Redeem 600 points
                for a free 60-minute massage!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Free Massages Available Card */}
            {canRedeem && (
              <Card className="border-2 border-palette-lime bg-gradient-to-r from-palette-lime/10 to-palette-navy/5">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-palette-lime flex items-center justify-center shadow-lg">
                      <Gift className="w-10 h-10 text-palette-navy" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-palette-phantom">
                        {rewards.freeMassagesAvailable} Free Massage Credit
                        {rewards.freeMassagesAvailable > 1 ? "s" : ""}{" "}
                        Available!
                      </h3>
                      <p className="text-palette-space mt-1">
                        You've redeemed points for free massage credits. Use
                        them when booking your next appointment!
                      </p>
                    </div>
                    <Button
                      size="lg"
                      onClick={handleRedeemFreeMassage}
                      disabled={
                        isRedeeming ||
                        (rewards?.currentPoints || 0) < POINTS_FOR_FREE_MASSAGE
                      }
                      className="bg-palette-lime hover:bg-palette-lime/90 text-palette-navy font-semibold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:bg-gray-300">
                      {isRedeeming ? "Redeeming..." : "Redeem Now"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Points Progress Card */}
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-palette-phantom">
                  Your Progress
                </CardTitle>
                <CardDescription className="text-palette-space">
                  {currentPoints} points earned from confirmed bookings •{" "}
                  {canRedeem
                    ? "Free massage credit available!"
                    : `${pointsToNext} points to next free massage`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-2xl font-bold text-palette-phantom">
                      {currentPoints} / {POINTS_FOR_FREE_MASSAGE}
                    </span>
                    <Badge className="bg-palette-lime text-palette-navy border-none font-semibold">
                      {pointsToNext} to go
                    </Badge>
                  </div>
                  <Progress value={progressPercentage} className="h-4" />
                  <div className="mt-3 text-center">
                    {canRedeem ? (
                      <>
                        <p className="text-sm font-semibold text-palette-lime mb-3">
                          🎉 You've earned enough points for a free massage!
                        </p>
                        <Button
                          onClick={handleRedeemFreeMassage}
                          disabled={
                            isRedeeming ||
                            (freeMassagesAvailable === 0 &&
                              currentPoints < POINTS_FOR_FREE_MASSAGE)
                          }
                          className="bg-palette-lime hover:bg-palette-lime/90 text-palette-navy font-semibold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:bg-gray-300">
                          {isRedeeming
                            ? "Redeeming..."
                            : freeMassagesAvailable > 0
                            ? "Use Free Massage Credit"
                            : "Redeem Points for Free Massage"}
                        </Button>
                        {freeMassagesAvailable === 0 &&
                          currentPoints >= POINTS_FOR_FREE_MASSAGE && (
                            <p className="text-xs text-palette-space mt-2">
                              Convert {POINTS_FOR_FREE_MASSAGE} points to a free
                              massage credit
                            </p>
                          )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-palette-space">
                          You have{" "}
                          <span className="font-semibold text-palette-phantom">
                            {currentPoints} points
                          </span>{" "}
                          from confirmed bookings
                        </p>
                        <p className="text-xs text-palette-space mt-1">
                          {pointsToNext} points left to earn a free massage
                          credit!
                        </p>
                        <Button
                          onClick={handleRedeemFreeMassage}
                          disabled={true}
                          className="mt-3 bg-gray-300 text-gray-500 font-semibold rounded-xl shadow-lg cursor-not-allowed opacity-50">
                          Redeem Free Massage
                        </Button>
                        <p className="text-xs text-palette-space mt-2">
                          Need {POINTS_FOR_FREE_MASSAGE} points to redeem.
                          Points are earned when bookings are confirmed by the
                          clinic.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Points Breakdown */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-palette-smoke/30">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-palette-lime">
                      {rewards?.totalPointsEarned || 0}
                    </div>
                    <div className="text-xs text-palette-space mt-1">
                      Total Points
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-palette-lime">
                      {rewards?.pointsRedeemed || 0}
                    </div>
                    <div className="text-xs text-palette-space mt-1">
                      Points Redeemed
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-palette-lime">
                      {rewards?.freeMassagesUsed || 0}
                    </div>
                    <div className="text-xs text-palette-space mt-1">
                      Free Massages Used
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How It Works */}
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-palette-phantom">
                  <Star className="w-5 h-5 text-palette-lime" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-palette-lime/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-palette-lime font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-palette-phantom">
                        Book Massages & Earn Points
                      </h4>
                      <p className="text-sm text-palette-space">
                        Earn 1 point for every minute of massage you book.
                        Points are awarded only when your booking is confirmed
                        by the clinic (60-min booking = 60 points).
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-palette-lime/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-palette-lime font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-palette-phantom">
                        Share Your Referral Code
                      </h4>
                      <p className="text-sm text-palette-space">
                        When someone books an appointment using your referral
                        code and their booking is confirmed by the clinic, you
                        earn points equal to their booking duration (e.g.,
                        90-min booking = 90 points for you).
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-palette-lime/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-palette-lime font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-palette-phantom">
                        Redeem Free Massages
                      </h4>
                      <p className="text-sm text-palette-space">
                        Every 600 points = 1 free 60-minute massage credit. You
                        can redeem points for free massage credits, which can be
                        used when booking your next appointment.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Points History */}
            {rewardsHistory.length > 0 && (
              <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-palette-phantom">
                    Recent Points Earned
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {rewardsHistory.map((entry, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-palette-cloud/50 rounded-lg hover:bg-palette-cloud/70 transition-colors">
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center",
                              entry.source === "referral"
                                ? "bg-palette-lime/20"
                                : "bg-palette-navy/10",
                            )}>
                            {entry.source === "referral" ? (
                              <Users className="w-5 h-5 text-palette-lime" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-palette-navy" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-palette-phantom">
                              {entry.source === "referral"
                                ? "Referral Bonus"
                                : "Massage Booking"}
                            </p>
                            <p className="text-xs text-palette-space">
                              {entry.bookingDate
                                ? new Date(
                                    entry.bookingDate,
                                  ).toLocaleDateString()
                                : entry.earnedAt
                                ? new Date(entry.earnedAt).toLocaleDateString()
                                : "Recently"}
                              {entry.bookingDuration &&
                                ` • ${entry.bookingDuration} min booking`}
                              {entry.source === "referral" && (
                                <span className="text-palette-lime ml-1">
                                  (Confirmed)
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-palette-lime text-palette-navy border-none font-semibold">
                          +{entry.pointsEarned} pts
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Redemptions */}
            {recentRedemptions.length > 0 && (
              <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-palette-phantom flex items-center gap-2">
                    <Gift className="w-5 h-5 text-palette-lime" />
                    Recent Redemptions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recentRedemptions.map((redemption, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-palette-lime/5 rounded-lg border border-palette-lime/20">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-palette-lime/20 flex items-center justify-center">
                            <Gift className="w-5 h-5 text-palette-lime" />
                          </div>
                          <div>
                            <p className="font-medium text-palette-phantom">
                              Free Massage Redeemed
                            </p>
                            <p className="text-xs text-palette-space">
                              {redemption.redeemedAt
                                ? new Date(
                                    redemption.redeemedAt,
                                  ).toLocaleDateString()
                                : "Recently"}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-palette-navy text-white border-none font-semibold">
                          -{redemption.pointsUsed} pts
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Referral Card */}
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-palette-phantom">
                  <Users className="w-5 h-5 text-palette-lime" />
                  Invite Friends
                </CardTitle>
                <CardDescription className="text-palette-space">
                  Share your referral code and earn points equal to the booking
                  duration when someone books using your code and their booking
                  is confirmed by the clinic!
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Referral Code */}
                {referralCode ? (
                  <>
                    <div className="p-4 bg-palette-lime/10 rounded-lg text-center border border-palette-lime/20">
                      <p className="text-xs text-palette-space mb-2 uppercase tracking-wide">
                        Your Referral Code
                      </p>
                      <p className="text-3xl font-bold text-palette-phantom font-mono mb-2">
                        {referralCode.referralCode}
                      </p>
                      <p className="text-xs text-palette-space">
                        Share this code or link to earn points!
                      </p>
                    </div>

                    {/* Referral Link */}
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={referralCode.referralLink || ""}
                          readOnly
                          className="flex-1 px-3 py-2 border border-palette-smoke/50 rounded-lg text-sm bg-palette-cloud/50 text-palette-phantom"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={copyReferralLink}
                          className="flex-shrink-0">
                          {copied ? (
                            <CheckCircle className="w-4 h-4 text-palette-lime" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <Button
                        onClick={shareReferralLink}
                        className="w-full bg-palette-lime hover:bg-palette-lime/90 text-palette-navy font-semibold">
                        <Share2 className="w-4 h-4 mr-2" />
                        Share Referral Link
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-palette-cloud/50 rounded-lg text-center border border-palette-smoke/30">
                    <p className="text-sm text-palette-space">
                      Loading referral code...
                    </p>
                  </div>
                )}

                {/* Referral Summary Stats */}
                {referralSummary && (
                  <div className="pt-4 border-t border-palette-smoke/30 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-palette-cloud/50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-palette-phantom">
                          {referralSummary.totalReferrals || 0}
                        </div>
                        <div className="text-xs text-palette-space mt-1">
                          Total Referrals
                        </div>
                      </div>
                      <div className="p-3 bg-palette-lime/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-palette-lime">
                          {referralSummary.completedReferrals || 0}
                        </div>
                        <div className="text-xs text-palette-space mt-1">
                          Completed
                        </div>
                      </div>
                    </div>
                    {referralSummary.totalPointsEarned > 0 && (
                      <div className="p-3 bg-palette-lime/5 rounded-lg border border-palette-lime/20">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-palette-phantom">
                            Points from Referrals
                          </span>
                          <Badge className="bg-palette-lime text-palette-navy border-none font-semibold">
                            {referralSummary.totalPointsEarned} pts
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Referral History */}
                {referralHistory.length > 0 && (
                  <div className="pt-4 border-t border-palette-smoke/30 space-y-2">
                    <p className="text-sm font-semibold text-palette-phantom mb-2">
                      Referral History
                    </p>
                    {referralHistory.slice(0, 5).map((ref, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg text-sm transition-colors",
                          ref.status === "completed"
                            ? "bg-palette-lime/5 border border-palette-lime/20"
                            : "bg-palette-cloud/30",
                        )}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-palette-phantom">
                              {ref.refereeName || "Pending"}
                            </p>
                            {ref.status === "completed" && (
                              <CheckCircle className="w-4 h-4 text-palette-lime" />
                            )}
                          </div>
                          <p className="text-xs text-palette-space mt-1">
                            {ref.status === "completed" ? (
                              <>
                                {ref.dateCompleted
                                  ? new Date(
                                      ref.dateCompleted,
                                    ).toLocaleDateString()
                                  : ref.bookingDate
                                  ? new Date(
                                      ref.bookingDate,
                                    ).toLocaleDateString()
                                  : "Completed"}
                                {ref.bookingDuration &&
                                  ` • ${ref.bookingDuration} min booking`}
                              </>
                            ) : (
                              "Pending completion"
                            )}
                          </p>
                          {ref.status === "completed" &&
                            ref.pointsEarned > 0 && (
                              <p className="text-xs text-palette-lime font-medium mt-1">
                                You earned {ref.pointsEarned} points (equal to{" "}
                                {ref.bookingDuration || ref.pointsEarned} min
                                booking duration) from{" "}
                                {ref.refereeName || "this referral"}
                              </p>
                            )}
                        </div>
                        {ref.status === "completed" && ref.pointsEarned > 0 && (
                          <Badge className="bg-palette-lime text-palette-navy border-none font-semibold ml-2">
                            +{ref.pointsEarned}
                          </Badge>
                        )}
                      </div>
                    ))}
                    {referralHistory.length > 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2 text-palette-space"
                        onClick={() => {
                          // Could add pagination or view all modal here
                          toast({
                            title: "View All",
                            description:
                              "Showing first 5 referrals. More features coming soon!",
                          });
                        }}>
                        View All ({referralHistory.length} total)
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats Summary */}
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-palette-phantom">
                  <TrendingUp className="w-5 h-5 text-palette-lime" />
                  Your Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-palette-space">
                    Free Massages Available
                  </span>
                  <Badge className="bg-palette-lime text-palette-navy border-none font-semibold">
                    {rewards?.freeMassagesAvailable || 0}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-palette-space">
                    Current Points
                  </span>
                  <span className="font-semibold text-palette-lime text-lg">
                    {currentPoints}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-palette-space">
                    Total Points Earned
                  </span>
                  <span className="font-semibold text-palette-phantom">
                    {rewards?.totalPointsEarned || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-palette-smoke/30">
                  <span className="text-sm text-palette-space">
                    Points Redeemed
                  </span>
                  <span className="font-semibold text-palette-phantom">
                    {rewards?.pointsRedeemed || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-palette-space">
                    Free Massages Used
                  </span>
                  <span className="font-semibold text-palette-phantom">
                    {rewards?.freeMassagesUsed || 0}
                  </span>
                </div>
                {referralSummary && referralSummary.totalPointsEarned > 0 && (
                  <div className="flex justify-between items-center pt-2 border-t border-palette-smoke/30">
                    <span className="text-sm text-palette-space">
                      From Referrals
                    </span>
                    <Badge className="bg-palette-lime/20 text-palette-lime border-none">
                      {referralSummary.totalPointsEarned} pts
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-gradient-to-br from-palette-lime to-palette-navy text-white border-none shadow-xl">
              <CardContent className="pt-6 text-center">
                <Sparkles className="w-12 h-12 mx-auto mb-3 text-white" />
                <h3 className="font-bold text-lg mb-2">
                  Book Your Next Massage
                </h3>
                <p className="text-white/90 text-sm mb-4">
                  Get closer to your next free massage!
                </p>
                <Button
                  onClick={() => navigate("/book")}
                  variant="secondary"
                  className="w-full bg-white hover:bg-white/90 text-palette-navy font-semibold">
                  Book Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
